import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import SearchableDropdown from '../../components/SearchableDropdown';
import DateTimePickerInput from '../../components/DateTimePickerInput';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useDynamicPermissions } from '../../hooks/useDynamicPermissions';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { dateToUTCNoon } from '../../utils/dateHelpers';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  displayName?: string;
}

interface Customer {
  id: string;
  accountName: string;
  accountShortName: string;
  effectiveDate: string;
  endDate: string | null;
  contactNumber: string;
  emailAddress: string;
  sbu: string;
  assignToUserScope: string;
  accountOwner: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  salesRepresentative: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface CustomerFormData {
  accountName: string;
  accountShortName: string;
  effectiveDate: string;
  endDate: string | null;
  contactNumber: string;
  emailAddress: string;
  sbu: string;
  fixedHoursBill: boolean;
  totalFixedHours: number | null;
  assignToUserScope: string;
  accountOwnerId: string;
  salesRepresentativeId: string;
}

interface CustomerFormErrors {
  emailAddress?: string;
  contactNumber?: string;
  endDate?: string;
  totalFixedHours?: string;
}

const CustomerForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { can, loading: permissionsLoading } = useDynamicPermissions();
  const { getToday } = useDateFormatter();

  const [formData, setFormData] = useState<CustomerFormData>({
    accountName: '',
    accountShortName: '',
    effectiveDate: '',
    endDate: null,
    contactNumber: '',
    emailAddress: '',
    sbu: 'BU1',
    fixedHoursBill: false,
    totalFixedHours: null,
    assignToUserScope: 'TENARAI_ONLY',
    accountOwnerId: '',
    salesRepresentativeId: ''
  });

  // Set default effective date on mount (for new customers)
  useEffect(() => {
    if (!isEdit && !formData.effectiveDate) {
      setFormData(prev => ({ ...prev, effectiveDate: getToday() }));
    }
  }, [isEdit, getToday]);

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<CustomerFormErrors>({});

  // Check permissions based on whether creating or editing
  const requiredAction = isEdit ? 'update' : 'create';
  const hasPermission = can('customers', requiredAction);

  useEffect(() => {
    // Wait for permissions to load before checking
    if (permissionsLoading) {
      return;
    }

    // Check if user has required permission
    if (!hasPermission) {
      navigate('/customers');
      return;
    }
    const fetchUsers = async () => {
      try {
        setUsersLoading(true);
        const response = await api.get('/customers/users/list');
        setUsers(response.data || []);
      } catch (err: any) {
        console.error('Failed to fetch users:', err);
        setUsers([]);
      } finally {
        setUsersLoading(false);
      }
    };

    const fetchCustomer = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/customers/${id}`);
        const customer: Customer = response.data;
        setFormData({
          accountName: customer.accountName,
          accountShortName: customer.accountShortName,
          effectiveDate: customer.effectiveDate.split('T')[0],
          endDate: customer.endDate ? customer.endDate.split('T')[0] : null,
          contactNumber: customer.contactNumber,
          emailAddress: customer.emailAddress,
          sbu: customer.sbu,
          fixedHoursBill: !!(customer as Customer & { fixedHoursBill?: boolean }).fixedHoursBill,
          totalFixedHours:
            (customer as Customer & { totalFixedHours?: number | null }).totalFixedHours != null
              ? Number((customer as Customer & { totalFixedHours?: number | null }).totalFixedHours)
              : null,
          assignToUserScope: customer.assignToUserScope || 'TENARAI_ONLY',
          accountOwnerId: customer.accountOwner.id,
          salesRepresentativeId: customer.salesRepresentative.id
        });
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to fetch customer');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
    if (isEdit) {
      fetchCustomer();
    }
  }, [id, hasPermission, isEdit, navigate, permissionsLoading]);

  const validateField = (name: string, value: string | null): string => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;

    switch (name) {
      case 'emailAddress':
        if (value && !emailRegex.test(value)) {
          return 'Invalid email format';
        }
        break;
      case 'contactNumber':
        if (value) {
          const cleanedPhone = value.replace(/\s+/g, '');
          if (!phoneRegex.test(cleanedPhone)) {
            return 'Invalid phone number format';
          }
          const digitCount = cleanedPhone.replace(/[^\d]/g, '').length;
          if (digitCount < 10) {
            return 'Phone number must have at least 10 digits';
          }
        }
        break;
      case 'endDate':
        if (value && formData.effectiveDate) {
          const endDate = new Date(value);
          const startDate = new Date(formData.effectiveDate);
          if (endDate <= startDate) {
            return 'End date must be greater than effective date';
          }
        }
        break;
    }
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    // Validate required fields
    // Company validation removed

    try {
      if (formData.fixedHoursBill) {
        if (formData.totalFixedHours == null || formData.totalFixedHours <= 0) {
          setErrors((prev) => ({
            ...prev,
            totalFixedHours: 'Total Fixed Hours is required and must be greater than 0',
          }));
          setSubmitting(false);
          return;
        }
      }

      const payload = {
        ...formData,
        effectiveDate: dateToUTCNoon(formData.effectiveDate),
        endDate: formData.endDate ? dateToUTCNoon(formData.endDate) : null,
        totalFixedHours: formData.fixedHoursBill ? formData.totalFixedHours : null,
      };

      if (isEdit) {
        await api.put(`/customers/${id}`, payload);
      } else {
        await api.post('/customers', payload);
      }
      navigate('/customers');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save customer');
      setSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const processedValue = value === '' && name === 'endDate' ? null : value;

    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    // Inline validation
    if (['emailAddress', 'contactNumber', 'endDate'].includes(name)) {
      const error = validateField(name, processedValue);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  

  const getUserOptions = () => {
    return users.map(user => ({
      value: user.id,
      label: user.displayName || `${user.firstName} ${user.lastName}`,
      subtitle: user.email
    }));
  };

  // Show loading while permissions are being checked
  if (permissionsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show permission denied if user doesn't have required permission
  if (!hasPermission) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          You don't have permission to {isEdit ? 'edit' : 'create'} customers.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-8 py-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/customers')}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Customers
          </button>
        </div>

        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h1 className="text-xl font-semibold text-gray-900">{isEdit ? 'Edit Customer' : 'New Customer'}</h1>
            <p className="mt-1 text-sm text-gray-600">{isEdit ? 'Update customer information' : 'Create a new customer account'}</p>
          </div>

          <form onSubmit={handleSubmit} className="p-8">
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Row 1: Customer Name and Short Name */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="accountName" className="block text-sm font-medium text-gray-700 mb-1">
                      Customer Name <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      id="accountName"
                      name="accountName"
                      value={formData.accountName}
                      onChange={handleChange}
                      required
                      placeholder="Full legal name of the customer"
                      className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="accountShortName" className="block text-sm font-medium text-gray-700 mb-1">
                      Short Name <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      id="accountShortName"
                      name="accountShortName"
                      value={formData.accountShortName}
                      onChange={handleChange}
                      required
                      placeholder="Abbreviated or display name"
                      className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Email Address and Contact Number */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="emailAddress" className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="email"
                      id="emailAddress"
                      name="emailAddress"
                      value={formData.emailAddress}
                      onChange={handleChange}
                      required
                      placeholder="Primary contact email"
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 ${
                        errors.emailAddress ? 'border-red-500 focus:ring-red-400' : 'border-gray-300 focus:ring-blue-400'
                      }`}
                    />
                    {errors.emailAddress && (
                      <p className="mt-1 text-xs text-red-600">{errors.emailAddress}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="contactNumber" className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Number <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="tel"
                      id="contactNumber"
                      name="contactNumber"
                      value={formData.contactNumber}
                      onChange={handleChange}
                      required
                      placeholder="Valid phone number"
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 ${
                        errors.contactNumber ? 'border-red-500 focus:ring-red-400' : 'border-gray-300 focus:ring-blue-400'
                      }`}
                    />
                    {errors.contactNumber && (
                      <p className="mt-1 text-xs text-red-600">{errors.contactNumber}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 3: Strategic Business Unit and Assign To User Scope */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="sbu" className="block text-sm font-medium text-gray-700 mb-1">
                      Strategic Business Unit
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      id="sbu"
                      name="sbu"
                      value={formData.sbu}
                      onChange={handleChange}
                      className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    >
                      <option value="BU1">BU1</option>
                      <option value="BU2">BU2</option>
                      <option value="BU3">BU3</option>
                      <option value="BU4">BU4</option>
                      <option value="BU5">BU5</option>
                      <option value="BU6">BU6</option>
                      <option value="BU7">BU7</option>
                      <option value="BU8">BU8</option>
                      <option value="BU9">BU9</option>
                      <option value="BU10">BU10</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="assignToUserScope" className="block text-sm font-medium text-gray-700 mb-1">
                      Assign To Users
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      id="assignToUserScope"
                      name="assignToUserScope"
                      value={formData.assignToUserScope}
                      onChange={handleChange}
                      className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    >
                      <option value="CONTACTS_ONLY">Customer Contacts Only</option>
                      <option value="TENARAI_AND_CONTACTS">Tenarai Users and Customer Contacts</option>
                      <option value="TENARAI_ONLY">Tenarai Users Only</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Controls which users appear in Assignment Group → Assign To dropdowns
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 4: Effective Date and End Date */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="effectiveDate" className="block text-sm font-medium text-gray-700 mb-1">
                      Effective Date <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <DateTimePickerInput
                      name="effectiveDate"
                      value={formData.effectiveDate}
                      onChange={(value) => setFormData(prev => ({ ...prev, effectiveDate: value }))}
                      showTimeSelect={false}
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                      End Date
                    </label>
                  </div>
                  <div className="col-span-9">
                    <DateTimePickerInput
                      name="endDate"
                      value={formData.endDate || ''}
                      onChange={(value) => {
                        const processedValue = value || null;
                        setFormData(prev => ({ ...prev, endDate: processedValue }));
                        // Validate end date
                        if (processedValue && formData.effectiveDate) {
                          const endDate = new Date(processedValue);
                          const startDate = new Date(formData.effectiveDate);
                          if (endDate <= startDate) {
                            setErrors(prev => ({ ...prev, endDate: 'End date must be greater than effective date' }));
                          } else {
                            setErrors(prev => ({ ...prev, endDate: undefined }));
                          }
                        } else {
                          setErrors(prev => ({ ...prev, endDate: undefined }));
                        }
                      }}
                      showTimeSelect={false}
                      className={errors.endDate ? 'border-red-500' : ''}
                    />
                    {errors.endDate && (
                      <p className="mt-1 text-xs text-red-600">{errors.endDate}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 5: Account Owner and Sales Representative */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Owner <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    {usersLoading ? (
                      <div className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-50 text-gray-500 text-sm h-8 flex items-center">
                        Loading users...
                      </div>
                    ) : (
                      <SearchableDropdown
                        value={formData.accountOwnerId || null}
                        onChange={(value) => setFormData(prev => ({ ...prev, accountOwnerId: value || '' }))}
                        options={getUserOptions()}
                        placeholder="Select account owner"
                        required
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sales Representative <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    {usersLoading ? (
                      <div className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-50 text-gray-500 text-sm h-8 flex items-center">
                        Loading users...
                      </div>
                    ) : (
                      <SearchableDropdown
                        value={formData.salesRepresentativeId || null}
                        onChange={(value) => setFormData(prev => ({ ...prev, salesRepresentativeId: value || '' }))}
                        options={getUserOptions()}
                        placeholder="Select sales representative"
                        required
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Billing Configuration */}
            <div className="grid grid-cols-12 gap-6 mb-6 pt-4 border-t border-gray-200">
              <div className="col-span-12">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Billing Configuration</h3>
              </div>
              <div className="col-span-6">
                <div className="flex items-center">
                  <input
                    id="fixedHoursBill"
                    name="fixedHoursBill"
                    type="checkbox"
                    checked={formData.fixedHoursBill}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData((prev) => ({
                        ...prev,
                        fixedHoursBill: checked,
                        totalFixedHours: checked ? prev.totalFixedHours : null,
                      }));
                      if (!checked) {
                        setErrors((prev) => ({ ...prev, totalFixedHours: undefined }));
                      }
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="fixedHoursBill" className="ml-2 block text-sm text-gray-900">
                    Fixed Hours Bill
                  </label>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  When enabled, included hours apply across all engagements for this customer.
                </p>
              </div>
              {formData.fixedHoursBill && (
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-4">
                      <label htmlFor="totalFixedHours" className="block text-sm font-medium text-gray-700 mb-1">
                        Total Fixed Hours <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-8">
                      <input
                        id="totalFixedHours"
                        name="totalFixedHours"
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={formData.totalFixedHours ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            totalFixedHours: v === '' ? null : parseFloat(v),
                          }));
                          if (v !== '' && parseFloat(v) > 0) {
                            setErrors((prev) => ({ ...prev, totalFixedHours: undefined }));
                          }
                        }}
                        className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                          errors.totalFixedHours ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                      />
                      {errors.totalFixedHours && (
                        <p className="mt-1 text-xs text-red-600">{errors.totalFixedHours}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/customers')}
                className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? `${isEdit ? 'Updating...' : 'Creating...'}` : `${isEdit ? 'Update' : 'Create'} Customer`}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default CustomerForm;